import React, {useMemo, useState} from "react";
import {Link} from "react-router-dom";
import {useSelector} from "react-redux";
import brokerTypes from "../../../constants/BrokerTypes";
import {currencyFormat} from "../../../utils/masks";
import {useTranslation} from "react-i18next";
import AlertDelete from "../../../components/Alert/AlertDelete";
import {FaCoins, FaHourglassStart, FaPercent, FaStore, FaWallet} from "react-icons/fa";
import DivDiagonal from "../../../components/DivDiagonal";
import {
    StatsIntro,
    StatStack,
    StatItem,
    StatIconWrap,
    StatText,
    StatLabel,
    StatHint,
    StatValue,
} from "../../../components/StatSummaryCard";
import {
    TradingWrapper,
    ContentArea,
    PageHeader as TradingPageHeader,
    HeaderActions,
    TradingCard,
    NewTradingButton,
    FilterTitle,
} from "../../Trading/styles";
import styled from "styled-components";

const ViewBankPageHeader = styled(TradingPageHeader)`
    z-index: 2;
    margin-bottom: 28px;

    @media (min-width: 768px) {
        margin-bottom: 36px;
    }
`;

const ViewBankNarrow = styled.div`
    max-width: 560px;
    margin-left: auto;
    margin-right: auto;
    width: 100%;
    position: relative;
    z-index: 2;
    margin-top: 16px;

    @media (min-width: 576px) {
        margin-top: 20px;
    }

    @media (min-width: 992px) {
        margin-top: 24px;
    }
`;

const ViewBankCard = styled(TradingCard)`
    position: relative;
    z-index: 2;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    padding: 20px 22px 22px;

    @media (min-width: 768px) {
        padding: 22px 26px 26px;
    }

    @media (min-width: 992px) {
        padding: 24px 28px 28px;
    }
`;

const CardSectionTitle = styled(FilterTitle)`
    margin-bottom: 14px;
`;

const MovementsHeaderButton = styled(NewTradingButton)`
    background: linear-gradient(135deg, rgba(10, 14, 39, 0.95) 0%, rgba(22, 33, 62, 0.98) 100%);
    border: none;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.35), 0 0 0 1px rgba(0, 0, 0, 0.2);
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.4);

    &:hover {
        background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
        box-shadow: 0 6px 20px rgba(0, 0, 0, 0.45), 0 0 16px rgba(242, 146, 5, 0.25);
    }
`;

const DeleteBankHeaderButton = styled.button`
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 50%, #b91c1c 100%);
    color: #fff !important;
    padding: 10px 20px;
    border-radius: 10px;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    box-shadow: 0 4px 14px rgba(220, 38, 38, 0.45);
    transition: all 0.3s ease;
    font-size: 14px;
    height: 44px;
    border: 1px solid #991b1b;
    cursor: pointer;

    @media (min-width: 768px) {
        padding: 12px 24px;
        gap: 8px;
        font-size: 16px;
        height: auto;
    }

    &:hover {
        background: linear-gradient(135deg, #f87171 0%, #ef4444 45%, #dc2626 100%);
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(220, 38, 38, 0.5);
        border-color: #7f1d1d;
        color: #fff !important;
    }
`;

export default function ViewBank() {
    const { t } = useTranslation();
    const [deleteBank, setDeleteBank] = useState(false);
    const {broker, initialValue, amount, coinType, investmentRate} = useSelector((state) => state.BankReducer);

    const initialNum = parseFloat(initialValue != null ? initialValue : 0);
    const amountNum = parseFloat(amount != null ? amount : 0);

    const currentValueColor = useMemo(() => {
        if (amountNum > initialNum) return "#8aedab";
        if (amountNum < initialNum) return "#ff9a9a";
        return "#e0e0e0";
    }, [amountNum, initialNum]);

    const currentAccent = useMemo(() => {
        if (amountNum > initialNum) return "rgba(91, 211, 124, 0.85)";
        if (amountNum < initialNum) return "rgba(255, 107, 107, 0.85)";
        return "rgba(0, 212, 255, 0.55)";
    }, [amountNum, initialNum]);

    function getBroker() {
        for (var index in brokerTypes) {
            if (brokerTypes[index].key === broker) {
                return brokerTypes[index].value;
            }
        }

        return t('bank_not_found');
    }

    const investmentAmount =
        investmentRate > 0 && amount ? parseFloat(investmentRate) * amountNum / 100 : 0;

    return (
        <TradingWrapper>
            <DivDiagonal />
            <ContentArea>
                <ViewBankPageHeader>
                    <h1>
                        <i className="fas fa-wallet" aria-hidden="true"/> {t('header_bank')}
                    </h1>
                    <HeaderActions>
                        <MovementsHeaderButton as={Link} to="/bank/movements">
                            <i className="fas fa-exchange-alt"/> {t('navbar_moviments')}
                        </MovementsHeaderButton>
                        <NewTradingButton as={Link} to="/bank">
                            <i className="fas fa-edit"/> {t('edit')}
                        </NewTradingButton>
                        <DeleteBankHeaderButton type="button" onClick={() => setDeleteBank(true)}>
                            <i className="fa fa-trash" aria-hidden="true"/> {t('profile_btn_delete_bank')}
                        </DeleteBankHeaderButton>
                    </HeaderActions>
                </ViewBankPageHeader>

                <ViewBankNarrow>
                    <ViewBankCard>
                        <CardSectionTitle>
                            <i className="fas fa-info-circle" aria-hidden="true"/> {t('bank_text_info')}
                        </CardSectionTitle>
                        <StatsIntro>{t('bank_view_intro')}</StatsIntro>
                        <StatStack>
                            <StatItem $accent="rgba(242, 146, 5, 0.9)">
                                <StatIconWrap
                                    $bg="rgba(242, 146, 5, 0.18)"
                                    $fg="#ffb347"
                                    $shadow="rgba(242, 146, 5, 0.25)"
                                >
                                    <FaStore aria-hidden="true"/>
                                </StatIconWrap>
                                <StatText>
                                    <StatLabel>{t('bank_broker')}</StatLabel>
                                    <StatHint>{t('bank_view_hint_broker')}</StatHint>
                                </StatText>
                                <StatValue $c="#f5f0e6">
                                    {getBroker()}
                                </StatValue>
                            </StatItem>

                            <StatItem $accent="rgba(0, 212, 255, 0.55)">
                                <StatIconWrap
                                    $bg="rgba(0, 212, 255, 0.12)"
                                    $fg="#7ae8ff"
                                    $shadow="rgba(0, 212, 255, 0.18)"
                                >
                                    <FaHourglassStart aria-hidden="true"/>
                                </StatIconWrap>
                                <StatText>
                                    <StatLabel>{t('bank_initial_value')}</StatLabel>
                                    <StatHint>{t('bank_view_hint_initial')}</StatHint>
                                </StatText>
                                <StatValue $c="#e0e0e0">
                                    {coinType} {currencyFormat(initialNum)}
                                </StatValue>
                            </StatItem>

                            <StatItem $accent={currentAccent}>
                                <StatIconWrap
                                    $bg={
                                        amountNum > initialNum
                                            ? "rgba(91, 211, 124, 0.15)"
                                            : amountNum < initialNum
                                              ? "rgba(255, 107, 107, 0.15)"
                                              : "rgba(0, 212, 255, 0.12)"
                                    }
                                    $fg={
                                        amountNum > initialNum
                                            ? "#8aedab"
                                            : amountNum < initialNum
                                              ? "#ff9a9a"
                                              : "#7ae8ff"
                                    }
                                    $shadow={
                                        amountNum > initialNum
                                            ? "rgba(91, 211, 124, 0.2)"
                                            : amountNum < initialNum
                                              ? "rgba(255, 107, 107, 0.2)"
                                              : "rgba(0, 212, 255, 0.18)"
                                    }
                                >
                                    <FaWallet aria-hidden="true"/>
                                </StatIconWrap>
                                <StatText>
                                    <StatLabel>{t('bank_current_value')}</StatLabel>
                                    <StatHint>{t('bank_view_hint_current')}</StatHint>
                                </StatText>
                                <StatValue $c={currentValueColor}>
                                    {coinType} {currencyFormat(amountNum)}
                                </StatValue>
                            </StatItem>

                            {investmentRate > 0 && amount ? (
                                <>
                                    <StatItem $accent="rgba(167, 139, 250, 0.75)">
                                        <StatIconWrap
                                            $bg="rgba(167, 139, 250, 0.15)"
                                            $fg="#c4b5fd"
                                            $shadow="rgba(167, 139, 250, 0.2)"
                                        >
                                            <FaPercent aria-hidden="true"/>
                                        </StatIconWrap>
                                        <StatText>
                                            <StatLabel>{t('bank_investment_rate')}</StatLabel>
                                            <StatHint>{t('bank_view_hint_rate')}</StatHint>
                                        </StatText>
                                        <StatValue $c="#e0e0e0">
                                            {parseFloat(investmentRate).toFixed(2)}%
                                        </StatValue>
                                    </StatItem>
                                    <StatItem $accent="rgba(251, 191, 36, 0.65)">
                                        <StatIconWrap
                                            $bg="rgba(251, 191, 36, 0.14)"
                                            $fg="#fcd34d"
                                            $shadow="rgba(251, 191, 36, 0.2)"
                                        >
                                            <FaCoins aria-hidden="true"/>
                                        </StatIconWrap>
                                        <StatText>
                                            <StatLabel>{t('bank_investment_value')}</StatLabel>
                                            <StatHint>{t('bank_view_hint_investment')}</StatHint>
                                        </StatText>
                                        <StatValue $c="#fde68a">
                                            {coinType} {currencyFormat(investmentAmount)}
                                        </StatValue>
                                    </StatItem>
                                </>
                            ) : null}
                        </StatStack>
                    </ViewBankCard>
                </ViewBankNarrow>
                {deleteBank ? (
                    <AlertDelete
                        functionCallbackCancel={() => setDeleteBank(false)}
                        functionCallbackConfirm={() => { window.location = '/'; }}
                        text={t('alert_remove_bank', { broker: getBroker() })}
                        urlDelete={process.env.REACT_APP_API_ENDPOINT + `/api/bank/delete`}
                    />
                ) : null}
            </ContentArea>
        </TradingWrapper>
    );
}
